import { useEffect, useRef, useState, useCallback } from "react";
import { gsap, prefersReducedMotion } from "../lib/motion";
import { scrollToSection } from "../lib/useSmoothScroll";
import { NAV_LINKS, LOGIN_URL } from "../data/site";
import Button from "./ui/Button";
import logoLight from "../assets/logo/logo-light.webp";
import "./Header.css";

export default function Header() {
  const headerRef = useRef(null);
  const panelRef = useRef(null);
  const lastY = useRef(0);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("#home");

  /* ---- hide-on-scroll-down / reveal-on-scroll-up + solid backdrop ---- */
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);

      if (!prefersReducedMotion() && !open) {
        const goingDown = y > lastY.current && y > 260;
        gsap.to(el, {
          yPercent: goingDown ? -100 : 0,
          duration: 0.55,
          ease: "power3.out",
          overwrite: "auto",
        });
      }
      lastY.current = y;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  /* ---- scroll-spy for the active nav underline ---- */
  useEffect(() => {
    const sections = NAV_LINKS.map((l) => document.querySelector(l.href)).filter(Boolean);
    if (!sections.length) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(`#${entry.target.id}`);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );

    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  /* ---- mobile panel open/close timeline ---- */
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;

    document.body.classList.toggle("is-locked", open);

    if (prefersReducedMotion()) {
      gsap.set(panel, { autoAlpha: open ? 1 : 0, clipPath: "none" });
      return undefined;
    }

    const items = panel.querySelectorAll("[data-mobile-item]");

    if (open) {
      gsap.set(panel, { autoAlpha: 1, pointerEvents: "auto" });
      gsap.fromTo(
        panel,
        { clipPath: "inset(0% 0% 100% 0%)" },
        { clipPath: "inset(0% 0% 0% 0%)", duration: 0.7, ease: "expo.out" }
      );
      gsap.fromTo(
        items,
        { yPercent: 110, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 0.75, stagger: 0.06, delay: 0.15, ease: "expo.out" }
      );
    } else {
      gsap.to(panel, {
        clipPath: "inset(0% 0% 100% 0%)",
        duration: 0.5,
        ease: "expo.in",
        onComplete: () => gsap.set(panel, { autoAlpha: 0, pointerEvents: "none" }),
      });
    }

    return undefined;
  }, [open]);

  /* ---- close on Escape ---- */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const go = useCallback((e, href) => {
    e.preventDefault();
    setOpen(false);
    // let the panel start closing before the scroll takes over
    requestAnimationFrame(() => scrollToSection(href));
  }, []);

  return (
    <>
      <header
        ref={headerRef}
        className={`header ${scrolled ? "is-scrolled" : ""} ${open ? "is-open" : ""}`}
      >
        <div className="container header__inner">
          <a className="header__logo" href="#home" onClick={(e) => go(e, "#home")} aria-label="Private Stays — home">
            <img src={logoLight} alt="Private Stays" width="601" height="165" />
          </a>

          <nav className="header__nav" aria-label="Primary">
            <ul>
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => go(e, link.href)}
                    className={active === link.href ? "is-active" : ""}
                    aria-current={active === link.href ? "page" : undefined}
                  >
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="header__actions">
            <Button href={LOGIN_URL} variant="ghost" className="header__login">
              Log in
              <span className="header__login-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8.4" r="3.6" stroke="currentColor" strokeWidth="1.8" />
                  <path
                    d="M4.8 20a7.2 7.2 0 0 1 14.4 0"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </Button>

            <button
              type="button"
              className="header__burger"
              aria-expanded={open}
              aria-controls="mobile-nav"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      <div className="mobile-nav" id="mobile-nav" ref={panelRef} aria-hidden={!open}>
        <div className="container mobile-nav__inner">
          <ul className="mobile-nav__list">
            {NAV_LINKS.map((link, i) => (
              <li key={link.href} className="mobile-nav__item">
                <a href={link.href} onClick={(e) => go(e, link.href)} data-mobile-item>
                  <em>{String(i + 1).padStart(2, "0")}</em>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="mobile-nav__foot" data-mobile-item>
            <Button href={LOGIN_URL} variant="solid" magnetic={false}>
              Log in
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
